import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import type {WorkstreamPillarLevelConfigAdminResponse} from 'client';
import {WorkstreamListService} from 'client';
import {getService} from 'utilities';
import type {WorkstreamTimeframe} from 'views/workstreams/detail/workstreamTimeframe';

const queryKey = (timeframe: WorkstreamTimeframe) => ['admin', 'pillar-level-configs', timeframe];
const publicPillarConfigQueryKey = ['workstream-data', 'pillar-configs'];

export function useAdminPillarLevelConfigs(
  timeframe: WorkstreamTimeframe,
  options?: {enabled?: boolean}
) {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: queryKey(timeframe),
    queryFn: () =>
      getService(
        WorkstreamListService
      ).listAdminPillarLevelConfigsApiV1AdminWorkstreamPillarLevelConfigsGet(timeframe),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateAdminPillarLevelConfig(timeframe: WorkstreamTimeframe) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pillarWorkstreamId,
      includePillarAsLevelOne,
    }: {
      pillarWorkstreamId: number;
      includePillarAsLevelOne: boolean;
    }) =>
      getService(
        WorkstreamListService
      ).updateAdminPillarLevelConfigApiV1AdminWorkstreamPillarLevelConfigsPillarWorkstreamIdPut(
        pillarWorkstreamId,
        {include_pillar_as_level_one: includePillarAsLevelOne},
        timeframe
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData<WorkstreamPillarLevelConfigAdminResponse[]>(
        queryKey(timeframe),
        (current) =>
          current?.map((config) =>
            config.pillar_workstream_id === updated.pillar_workstream_id ? updated : config
          ) ?? [updated]
      );
      await queryClient.invalidateQueries({queryKey: publicPillarConfigQueryKey});
    },
  });
}
